/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Select } from '../Select/Select'
import { InfiniteScrollSelect } from '../InfiniteScrollSelect'
import { documentTypesApiService } from '../../services/api/api.documenttypes'

/**
 * Document Type Dropdown Component
 * Ported from lego/component/doc-type-dropdown/DocTypeDropdown.jsx
 *
 * Supports:
 * - Async loading with infinite scroll
 * - Filtered by allowed types
 * - Single and multi-select modes
 */

interface DocTypeDropdownProps {
  id?: string
  name?: string
  placeholder?: string
  required?: boolean
  isSingle?: boolean
  disabled?: boolean
  allowedTypes?: number[]
  onChange?: (option: any) => void
  value?: any
}

export const DocTypeDropdown: React.FC<DocTypeDropdownProps> = ({
  id,
  name,
  placeholder = 'SELECT_DOC_TYPES_PLACEHOLDER...',
  required = false,
  isSingle = true,
  disabled = false,
  allowedTypes,
  onChange,
  value
}) => {
  const { t: translate } = useTranslation()
  const [showAsync, setShowAsync] = useState(true)
  const [availableTypes, setAvailableTypes] = useState<any[]>([])
  const [selectedValue, setSelectedValue] = useState<any>(value)
  const hasAutoSelected = useRef(false)

  useEffect(() => {
    setSelectedValue(value)
  }, [value])

  useEffect(() => {
    // If allowedTypes is specified, load them directly
    if (Array.isArray(allowedTypes) && allowedTypes.length > 0) {
      // Reset auto-selection flag when allowedTypes changes
      hasAutoSelected.current = false

      documentTypesApiService
        .getAllForAsyncPicker('', allowedTypes)
        .then((data) => {
          setShowAsync(false)
          setAvailableTypes(data)

          // Auto-select if only one option and hasn't been auto-selected yet
          if (data.length === 1 && onChange && !hasAutoSelected.current) {
            hasAutoSelected.current = true
            onChange({ ...data[0] })
          }
        })
        .catch((error) => {
          console.error('Failed to load document types:', error)
        })
    }
  }, [allowedTypes]) // Removed onChange from deps - only run when allowedTypes changes

  const handleChange = (option: any) => {
    setSelectedValue(option)
    if (onChange) {
      onChange(option)
    }
  }

  const asyncLoadFn = async (search: string, page: number) => {
    try {
      const response = (await documentTypesApiService.getOptionsForAsyncPicker(search, page)) as {
        options?: any[]
        hasMore?: boolean
      }
      return {
        options: response.options || [],
        hasMore: response.hasMore || false
      }
    } catch (error) {
      console.error('Failed to load document types:', error)
      return { options: [], hasMore: false }
    }
  }

  // Show async picker when no allowed types specified
  if (showAsync) {
    if (isSingle) {
      return (
        <InfiniteScrollSelect
          placeholder={translate(placeholder)}
          name={name}
          value={selectedValue}
          asyncLoadFn={asyncLoadFn}
          onChange={handleChange}
          searchable
          closeMenuOnSelect
          isClearable={false}
          isDisabled={disabled}
          id={id || 'b4a34900-933e-439c-a88b-a92880a90074'}
        />
      )
    }

    // Multi-select mode for async
    return (
      <InfiniteScrollSelect
        placeholder={translate(placeholder)}
        name={name}
        value={selectedValue}
        asyncLoadFn={asyncLoadFn}
        onChange={handleChange}
        isMulti
        searchable
        closeMenuOnSelect
        isClearable={false}
        isDisabled={disabled}
        id={id || '08cb04df-c00d-4e2b-b7fd-284f513fb7cd'}
      />
    )
  }

  // Show static select when allowed types are loaded
  if (availableTypes.length === 1) {
    return <p>{availableTypes[0]?.label}</p>
  }

  // Select component expects a primitive value (it resolves the full option object
  // from the options array internally via options.find(x => x.value === value)).
  // If selectedValue is a full option object, extract its .value for matching.
  const selectValue =
    selectedValue && typeof selectedValue === 'object' && 'value' in selectedValue ? selectedValue.value : selectedValue

  return (
    <Select
      id={id || `doctypes_select_${Math.random()}`}
      placeholder={translate(placeholder)}
      required={required}
      options={availableTypes}
      value={selectValue}
      isClearable={false}
      isDisabled={disabled}
      isMulti={!isSingle}
      onChange={handleChange}
    />
  )
}

export default DocTypeDropdown
